import {
  DEFAULT_CORE_GROUP_WORKFLOW_STATUS,
  isCoreGroupWorkflowStatus,
  normalizeCoreGroupWorkflowStatus
} from './core_group_workflow_model.mjs?v=20260903a';

const CORE_GROUP_TITLE_SCHEMA_VERSION = 1;
const CORE_GROUP_TITLE_SUFFIX_MAX_LENGTH = 120;

function normalizedText(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

function positiveInteger(value, fallback = null) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : fallback;
}

function uniqueTexts(values = []) {
  const result = [];
  const add = value => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    const text = normalizedText(value);
    if (text && !result.includes(text)) result.push(text);
  };
  add(values);
  return result;
}

function defaultCoreGroupTitleSuffix(number) {
  return `Nr${positiveInteger(number, 1)}`;
}

function validateCoreGroupTitleSuffix(value, options = {}) {
  const maxLength = positiveInteger(options.maxLength, CORE_GROUP_TITLE_SUFFIX_MAX_LENGTH);
  const suffix = normalizedText(value);
  if (!suffix) {
    return {
      valid: false,
      suffix: '',
      error: 'Der Gruppenname nach dem Systemnamen darf nicht leer sein.'
    };
  }
  if (suffix.length > maxLength) {
    return {
      valid: false,
      suffix,
      error: `Der Gruppenname darf höchstens ${maxLength} Zeichen enthalten.`
    };
  }
  if ([...suffix].some(character => {
    const codePoint = character.codePointAt(0);
    return codePoint <= 31 || codePoint === 127;
  })) {
    return {
      valid: false,
      suffix,
      error: 'Der Gruppenname enthält ungültige Steuerzeichen.'
    };
  }
  return { valid: true, suffix, error: '' };
}

function coreGroupFullTitle(baseLabel, suffix) {
  const normalizedBase = normalizedText(baseLabel) || 'Gruppe';
  const normalizedSuffix = normalizedText(suffix);
  return normalizedSuffix ? `${normalizedBase} ${normalizedSuffix}` : normalizedBase;
}

function normalizeCoreGroupTitleEntry(value = {}) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const number = positiveInteger(value.number ?? value.sequenceNumber);
  if (!number) return null;
  const fallbackSuffix = defaultCoreGroupTitleSuffix(number);
  const suffixValidation = validateCoreGroupTitleSuffix(value.suffix ?? value.titleSuffix ?? fallbackSuffix);
  const suffix = suffixValidation.valid ? suffixValidation.suffix : fallbackSuffix;
  const currentGroupKey = normalizedText(value.currentGroupKey ?? value.groupKey ?? value.key);
  const anchorId = normalizedText(value.anchorId ?? value.primaryObjectId ?? value.sourceId ?? value.groupId);
  const memberIds = uniqueTexts([
    value.memberIds,
    value.objectIds,
    value.bomIds,
    value.renderIds,
    anchorId
  ]);
  const baseLabel = normalizedText(value.baseLabel ?? value.groupTitleBase ?? value.systemLabel) || 'Gruppe';
  return {
    id: normalizedText(value.id) || `core_group_title_${number}`,
    number,
    currentGroupKey,
    anchorId,
    memberIds,
    systemId: normalizedText(value.systemId ?? value.systemKey),
    baseLabel,
    suffix,
    customized: value.customized === true || suffix !== fallbackSuffix,
    workflowStatus: normalizeCoreGroupWorkflowStatus(value.workflowStatus),
    ...(typeof value.geometryKey==='string' ? {geometryKey:value.geometryKey,workflowGeometryKey:typeof value.workflowGeometryKey==='string' ? value.workflowGeometryKey : value.geometryKey} : {})
  };
}

function normalizeCoreGroupTitleEntries(value = []) {
  if (!Array.isArray(value)) return [];
  const entries = [];
  const usedIds = new Set();
  const usedNumbers = new Set();
  for (const rawEntry of value) {
    const entry = normalizeCoreGroupTitleEntry(rawEntry);
    if (!entry || usedIds.has(entry.id) || usedNumbers.has(entry.number)) continue;
    usedIds.add(entry.id);
    usedNumbers.add(entry.number);
    entries.push(entry);
  }
  return entries.sort((left, right) => left.number - right.number);
}

function normalizeNextCoreGroupNumber(value, entries = []) {
  const highestStoredNumber = normalizeCoreGroupTitleEntries(entries)
    .reduce((highest, entry) => Math.max(highest, entry.number), 0);
  return Math.max(positiveInteger(value, 1), highestStoredNumber + 1);
}

function coreGroupDescriptor(group = {}, index = 0) {
  const systemId = normalizedText(group.systemId ?? group.systemKey);
  const groupId = normalizedText(group.groupId ?? group.id);
  const currentGroupKey = normalizedText(group.key) || (systemId && groupId ? `${systemId}:${groupId}` : '');
  if (!systemId || !groupId || currentGroupKey !== `${systemId}:${groupId}`) return null;
  const anchorId = normalizedText(
    group.primaryObjectId
      ?? group.sourceId
      ?? groupId
  );
  const memberIds = uniqueTexts([
    group.memberIds,
    group.objectIds,
    group.bomIds,
    group.renderIds,
    group.primaryObjectId,
    anchorId,
    groupId
  ]);
  const baseLabel = normalizedText(
    group.groupTitleBase
      ?? group.baseDisplayName
      ?? group.systemLabel
      ?? group.displayName
      ?? group.label
      ?? group.name
      ?? systemId
  ) || `Gruppe ${index + 1}`;
  return {
    currentGroupKey,
    anchorId,
    memberIds,
    systemId,
    baseLabel,
    strictIdentity: group.meta?.titleIdentity==='key',
    ...(typeof group.meta?.geometryKey==='string' ? {geometryKey:group.meta.geometryKey} : {})
  };
}

function entryMatchScore(entry, descriptor) {
  if (!entry || !descriptor) return null;
  if (!entry.systemId || entry.systemId !== descriptor.systemId) return null;
  if (entry.currentGroupKey && entry.currentGroupKey === descriptor.currentGroupKey) {
    return { rank: 3, overlap: Number.MAX_SAFE_INTEGER };
  }
  if (descriptor.strictIdentity) return null;
  if (entry.anchorId && descriptor.anchorId && entry.anchorId === descriptor.anchorId) {
    return { rank: 2, overlap: Number.MAX_SAFE_INTEGER };
  }
  const descriptorMembers = new Set(descriptor.memberIds);
  const overlap = entry.memberIds.reduce((sum, id) => sum + (descriptorMembers.has(id) ? 1 : 0), 0);
  return overlap > 0 ? { rank: 1, overlap } : null;
}

function matchingEntry(entries, descriptor, claimedEntryIds = new Set()) {
  return entries
    .filter(entry => !claimedEntryIds.has(entry.id))
    .map(entry => ({ entry, score: entryMatchScore(entry, descriptor) }))
    .filter(candidate => candidate.score)
    .sort((left, right) => (
      right.score.rank - left.score.rank
      || right.score.overlap - left.score.overlap
      || left.entry.number - right.entry.number
    ))[0]?.entry || null;
}

function createCoreGroupTitleEntry(descriptor, number, options = {}) {
  const normalizedNumber = positiveInteger(number, 1);
  const requestedSuffix = options.customized === true
    ? normalizedText(options.suffix)
    : defaultCoreGroupTitleSuffix(normalizedNumber);
  const validation = validateCoreGroupTitleSuffix(requestedSuffix);
  const suffix = validation.valid ? validation.suffix : defaultCoreGroupTitleSuffix(normalizedNumber);
  return {
    id: `core_group_title_${normalizedNumber}`,
    number: normalizedNumber,
    currentGroupKey: descriptor.currentGroupKey,
    anchorId: descriptor.anchorId,
    memberIds: descriptor.memberIds.slice(),
    systemId: descriptor.systemId,
    baseLabel: descriptor.baseLabel,
    suffix,
    customized: options.customized === true && suffix !== defaultCoreGroupTitleSuffix(normalizedNumber),
    // Neue logische Gruppen beginnen immer im Entwurf. Insbesondere darf eine
    // kopierte Zeichnungsebene spaeter keinen sperrenden Bestellstatus erben.
    workflowStatus: DEFAULT_CORE_GROUP_WORKFLOW_STATUS,
    ...(descriptor.geometryKey!==undefined ? {geometryKey:descriptor.geometryKey,workflowGeometryKey:descriptor.geometryKey} : {})
  };
}

function updateEntryFromDescriptor(entry, descriptor) {
  return {
    ...entry,
    currentGroupKey: descriptor.currentGroupKey,
    anchorId: descriptor.anchorId,
    memberIds: descriptor.memberIds.slice(),
    systemId: descriptor.systemId,
    baseLabel: descriptor.baseLabel,
    ...(descriptor.geometryKey!==undefined ? {geometryKey:descriptor.geometryKey,
      workflowGeometryKey:entry.workflowGeometryKey ?? descriptor.geometryKey} : {})
  };
}

function decorateCoreGroup(group, entry) {
  const displayName = coreGroupFullTitle(entry.baseLabel, entry.suffix);
  return {
    ...group,
    groupTitleEntryId: entry.id,
    groupTitleBase: entry.baseLabel,
    groupTitleSuffix: entry.suffix,
    groupNumber: entry.number,
    workflowStatus: entry.geometryKey!==undefined && entry.geometryKey!==entry.workflowGeometryKey ? DEFAULT_CORE_GROUP_WORKFLOW_STATUS : entry.workflowStatus,
    displayName,
    label: displayName
  };
}

function reconcileCoreGroupTitles(entriesValue = [], groupsValue = [], nextNumberValue = 1) {
  const entries = normalizeCoreGroupTitleEntries(entriesValue);
  const groups = Array.isArray(groupsValue) ? groupsValue : [];
  let nextNumber = normalizeNextCoreGroupNumber(nextNumberValue, entries);
  let changed = JSON.stringify(entries) !== JSON.stringify(Array.isArray(entriesValue) ? entriesValue : []);
  const claimedEntryIds = new Set();
  const decoratedGroups = [];

  groups.forEach((group, index) => {
    const descriptor = coreGroupDescriptor(group, index);
    if (!descriptor) return;
    let entry = matchingEntry(entries, descriptor, claimedEntryIds);
    if (!entry) {
      entry = createCoreGroupTitleEntry(descriptor, nextNumber);
      nextNumber += 1;
      entries.push(entry);
      changed = true;
    } else {
      const updated = updateEntryFromDescriptor(entry, descriptor);
      if (JSON.stringify(updated) !== JSON.stringify(entry)) {
        Object.assign(entry, updated);
        changed = true;
      }
    }
    claimedEntryIds.add(entry.id);
    decoratedGroups.push(decorateCoreGroup(group, entry));
  });

  const normalizedNextNumber = normalizeNextCoreGroupNumber(nextNumber, entries);
  if (normalizedNextNumber !== positiveInteger(nextNumberValue, 1)) changed = true;
  return {
    schemaVersion: CORE_GROUP_TITLE_SCHEMA_VERSION,
    entries: entries.sort((left, right) => left.number - right.number),
    groups: decoratedGroups,
    nextNumber: normalizedNextNumber,
    changed
  };
}

function copyCoreGroupTitles(entriesValue = [], groupsValue = [], nextNumberValue = 1) {
  const sourceEntries = normalizeCoreGroupTitleEntries(entriesValue);
  const groups = Array.isArray(groupsValue) ? groupsValue : [];
  const claimedEntryIds = new Set();
  const copiedEntries = [];
  let nextNumber = normalizeNextCoreGroupNumber(nextNumberValue, sourceEntries);

  groups.forEach((group, index) => {
    const descriptor = coreGroupDescriptor(group, index);
    if (!descriptor) return;
    const sourceEntry = matchingEntry(sourceEntries, descriptor, claimedEntryIds);
    if (sourceEntry) claimedEntryIds.add(sourceEntry.id);
    const copiedEntry = createCoreGroupTitleEntry(descriptor, nextNumber, {
      customized: sourceEntry?.customized === true,
      suffix: sourceEntry?.suffix
    });
    copiedEntries.push(copiedEntry);
    nextNumber += 1;
  });

  return {
    schemaVersion: CORE_GROUP_TITLE_SCHEMA_VERSION,
    entries: copiedEntries,
    groups: groups.map((group, index) => {
      const entry = copiedEntries[index];
      return entry ? decorateCoreGroup(group, entry) : group;
    }),
    nextNumber: normalizeNextCoreGroupNumber(nextNumber, copiedEntries)
  };
}

function updateCoreGroupTitleSuffix(entriesValue, entryId, value) {
  const validation = validateCoreGroupTitleSuffix(value);
  if (!validation.valid) throw new Error(validation.error);
  const entries = normalizeCoreGroupTitleEntries(entriesValue);
  const id = normalizedText(entryId);
  const entry = entries.find(candidate => candidate.id === id);
  if (!entry) throw new Error('Die ausgewählte Gruppe wurde nicht gefunden.');
  entry.suffix = validation.suffix;
  entry.customized = validation.suffix !== defaultCoreGroupTitleSuffix(entry.number);
  return {
    entries,
    entry,
    displayName: coreGroupFullTitle(entry.baseLabel, entry.suffix)
  };
}

function updateCoreGroupWorkflowStatus(entriesValue, entryId, value) {
  if (!isCoreGroupWorkflowStatus(value)) {
    throw new Error('Der gewählte Gruppenstatus ist ungültig.');
  }
  const entries = normalizeCoreGroupTitleEntries(entriesValue);
  const id = normalizedText(entryId);
  const entry = entries.find(candidate => candidate.id === id);
  if (!entry) throw new Error('Die ausgewählte Gruppe wurde nicht gefunden.');
  const workflowStatus = normalizeCoreGroupWorkflowStatus(value);
  const changed = entry.workflowStatus !== workflowStatus || entry.geometryKey!==entry.workflowGeometryKey;
  entry.workflowStatus = workflowStatus;
  if(entry.geometryKey!==undefined)entry.workflowGeometryKey=entry.geometryKey;
  return { entries, entry, workflowStatus, changed };
}

export {
  CORE_GROUP_TITLE_SCHEMA_VERSION,
  CORE_GROUP_TITLE_SUFFIX_MAX_LENGTH,
  copyCoreGroupTitles,
  coreGroupDescriptor,
  coreGroupFullTitle,
  defaultCoreGroupTitleSuffix,
  normalizeCoreGroupTitleEntries,
  normalizeNextCoreGroupNumber,
  reconcileCoreGroupTitles,
  updateCoreGroupWorkflowStatus,
  updateCoreGroupTitleSuffix,
  validateCoreGroupTitleSuffix
};
